import { Link, router } from '@inertiajs/react'
import { useState } from 'react'
import GoogleMap from '../../components/GoogleMap'

export default function Show({ offer }) {
  const [accepting, setAccepting] = useState(false)
  const [accepted, setAccepted] = useState(false)
  const [queueInfo, setQueueInfo] = useState(null)

  const typeColors = {
    gaming: 'bg-purple-100 text-purple-800',
    dining: 'bg-orange-100 text-orange-800',
    spa: 'bg-blue-100 text-blue-800',
    entertainment: 'bg-green-100 text-green-800'
  }

  const toNumber = (value) => {
    const parsed = typeof value === 'number' ? value : Number.parseFloat(value)
    return Number.isFinite(parsed) ? parsed : null
  }

  const offerPoint = (() => {
    const offerLat = toNumber(offer.lat)
    const offerLng = toNumber(offer.lng)
    if (offerLat !== null && offerLng !== null) {
      return [{
        id: offer.id,
        title: offer.offer_name,
        lat: offerLat,
        lng: offerLng,
        infoHtml: `<div><strong>${offer.offer_name}</strong><br/>${offer.brand || ''}</div>`,
      }]
    }
    const venueLat = toNumber(offer.venue?.latitude)
    const venueLng = toNumber(offer.venue?.longitude)
    if (offer.venue && venueLat !== null && venueLng !== null) {
      return [{
        id: offer.id,
        title: offer.offer_name,
        lat: venueLat,
        lng: venueLng,
        infoHtml: `<div><strong>${offer.offer_name}</strong><br/>${offer.venue.name}</div>`,
      }]
    }
    return []
  })()

  const handleAcceptOffer = async () => {
    setAccepting(true)
    try {
      const response = await fetch('/queues', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({ offer_id: offer.id })
      })
      const data = await response.json()
      if (data.success) {
        setAccepted(true)
        setQueueInfo({ id: data.queue.id, position: data.queue.position })
        setTimeout(() => {
          router.visit(`/queues/${data.queue.id}?accepted=1`)
        }, 8000)
      } else {
        alert('Error: ' + (data.errors || ['Failed to join queue']).join(', '))
      }
    } catch (error) {
      alert('Network error. Please try again.')
      console.error('Queue creation error:', error)
    } finally {
      setAccepting(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <Link 
            href="/" 
            className="inline-flex items-center text-blue-600 hover:text-blue-800 text-lg"
          >
            ← Back to Offers
          </Link>
          <div className="flex gap-3">
            <Link
              href="/"
              className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-2 px-4 rounded-lg"
            >
              Offers
            </Link>
            <Link
              href="/queues"
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg"
            >
              Active Queues
            </Link>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {offer.offer_marker_image_addr && (
            <img 
              src={offer.offer_marker_image_addr} 
              alt={offer.offer_name}
              className="w-full h-96 object-cover"
            />
          )}

          <div className="p-10">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h1 className="text-5xl font-bold text-gray-900 mb-4">
                  {offer.offer_name}
                </h1>
                <span className={`inline-block px-4 py-2 rounded-full text-sm font-semibold ${typeColors[offer.offer_type] || 'bg-gray-100 text-gray-800'}`}>
                  {offer.offer_type}
                </span>
              </div>
            </div>

            {offer.brand && (
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">Brand</h3>
                <p className="text-2xl text-gray-900">{offer.brand}</p>
              </div>
            )}

            {offer.description && (
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">Description</h3>
                <p className="text-xl text-gray-700 leading-relaxed">{offer.description}</p>
              </div>
            )}

            {offer.venue && (
              <div className="mb-8 bg-gray-50 p-6 rounded-lg">
                <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Venue</h3>
                <p className="text-xl text-gray-900 mb-2">{offer.venue.name}</p>
                {offer.venue.address && (
                  <p className="text-gray-600">{offer.venue.address}</p>
                )}
              </div>
            )}

            <div className="mb-8 bg-white rounded-xl shadow p-4">
              <GoogleMap
                points={offerPoint}
                className="h-80 w-full rounded-lg"
              />
            </div>

            {queueInfo && (
              <div className="mb-6 rounded-lg border border-green-200 bg-green-50 p-4 flex items-center justify-between">
                <div>
                  <p className="text-green-800 font-semibold">Success! You're in position {queueInfo.position}.</p>
                  <p className="text-green-700 text-sm">Redirecting to your active queue in 8 seconds...</p>
                </div>
                <button
                  onClick={() => router.visit(`/queues/${queueInfo.id}?accepted=1`)}
                  className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-lg"
                >
                  Go to Queue
                </button>
              </div>
            )}

            <div className="flex gap-4 pt-6 border-t border-gray-200">
              <Link
                href={`/menus/${offer.id}`}
                className="flex-1 text-center font-bold py-4 px-8 rounded-xl text-xl transition-colors bg-blue-600 hover:bg-blue-700 text-white"
              >
                View Menu
              </Link>
              <button
                onClick={handleAcceptOffer}
                disabled={accepting || accepted}
                className={`font-bold py-4 px-8 rounded-xl text-xl transition-colors ${
                  accepted
                    ? 'bg-green-600 text-white cursor-not-allowed'
                    : accepting
                    ? 'bg-blue-400 text-white cursor-wait'
                    : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
                }`}
              >
                {accepted ? '✓ Accepted' : accepting ? 'Processing...' : 'Quick Queue'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
